import React from "react";
import { Link } from "react-router-dom";

const FIELD_LABELS = {
  subweight: "Subweight",
  ptAssay: "Pt Assay",
  pdAssay: "Pd Assay",
  rhAssay: "Rh Assay",
};

const BUCKET_ORDER = ["0-5", "5-20", "20-50", "above50"];

const BUCKET_STYLES = {
  "0-5": { bg: "#d4edda", border: "#b8dfc4", color: "#1e6b3a", label: "Low (0–5%)" },
  "5-20": { bg: "#fff3cd", border: "#f0e0a0", color: "#856404", label: "Moderate (5–20%)" },
  "20-50": { bg: "#ffe0b2", border: "#f5c98a", color: "#c25e00", label: "High (20–50%)" },
  above50: { bg: "#f8d7da", border: "#f0b8be", color: "#9b1c2e", label: "Very high (50%+)" },
};

function bucketClassName(bucketId) {
  return `bucket-${bucketId.replace(/-/g, "")}`;
}

function formatNumber(value) {
  if (value == null || Number.isNaN(Number(value))) return "—";
  const n = Number(value);
  if (Math.abs(n) >= 1000) return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
  return n.toLocaleString(undefined, { maximumFractionDigits: 4 });
}

export function AssayDriftBuckets({ summary, selectedField, selectedBucket, onSelect, onClear }) {
  const driftByField = summary?.driftByField || [];
  if (!driftByField.length) {
    return <div className="c-gray fs-14">No assay drift data available.</div>;
  }

  return (
    <div className="converter-testing-drift-buckets">
      <div className="converter-testing-executive-title">Drift by assay field</div>
      <p className="converter-testing-drift-help c-gray fs-13 mb-16">
        Drift % = |test average − catalog| ÷ catalog × 100. Click a bucket to list OEMs.
      </p>
      <div className="converter-testing-heatmap-legend mb-16">
        {BUCKET_ORDER.map((bucketId) => {
          const style = BUCKET_STYLES[bucketId];
          return (
            <div key={bucketId} className="converter-testing-heatmap-legend-item">
              <span
                className="converter-testing-heatmap-swatch"
                style={{ background: style.bg, borderColor: style.border }}
              />
              <span className="fs-12">{style.label}</span>
            </div>
          );
        })}
      </div>
      {driftByField.map((fieldRow) => (
        <div key={fieldRow.field} className="converter-testing-drift-field-block">
          <div className="converter-testing-drift-field-title">{fieldRow.label || FIELD_LABELS[fieldRow.field]}</div>
          <div className="converter-testing-drift-bucket-row">
            {BUCKET_ORDER.map((bucketId) => {
              const bucket = (fieldRow.buckets || []).find((b) => b.id === bucketId);
              const isActive =
                selectedField === fieldRow.field && selectedBucket === bucketId;
              return (
                <button
                  key={`${fieldRow.field}-${bucketId}`}
                  type="button"
                  className={`converter-testing-drift-bucket ${bucketClassName(bucketId)} ${isActive ? "active" : ""}`}
                  onClick={() => onSelect(fieldRow.field, bucketId)}
                >
                  <div className="converter-testing-drift-bucket-label">
                    {bucket?.label || bucketId}
                  </div>
                  <div className="converter-testing-drift-bucket-count">
                    {(bucket?.count ?? 0).toLocaleString()}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ))}
      {selectedField && selectedBucket && (
        <button type="button" className="converter-testing-details-toggle" onClick={onClear}>
          ✕ Clear selection
        </button>
      )}
    </div>
  );
}

export function AssayDriftOemList({ items, driftField, contextLabel, totalCount, offset, pageSize, areMoreItemsAvailable, onPrev, onNext }) {
  if (!driftField) return null;

  const label = FIELD_LABELS[driftField] || driftField;
  const from = totalCount === 0 ? 0 : offset + 1;
  const to = offset + items.length;

  return (
    <div className="converter-testing-drift-oem-list">
      <div className="c-red fw-bolder mb-10 fs-14">
        OEMs{contextLabel ? ` — ${contextLabel}` : ""} · {label} drift
      </div>
      <div className="converter-testing-details-meta">
        Showing {from}–{to} of {totalCount.toLocaleString()} converters
      </div>
      {!items.length ? (
        <div className="c-gray fs-14">No converters in this bucket.</div>
      ) : (
        <div className="converter-testing-drift-wrap">
          <table className="converter-testing-drift-table">
            <thead>
              <tr>
                <th>OEM</th>
                <th>Type</th>
                <th>Catalog</th>
                <th>Test avg</th>
                <th>Drift %</th>
                <th>Delta</th>
                <th>Tests</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const catalog = item.productAssay?.[driftField];
                const testAvg = item.testAvg?.[driftField];
                const delta = item.drift?.[driftField];
                const driftPct = item.driftPct?.[driftField];
                return (
                  <tr key={item.productId}>
                    <td>
                      <Link className="converter-testing-link" to={`/converter/${item.productId}`}>
                        {item.oem || item.productId}
                      </Link>
                    </td>
                    <td>{item.type || "—"}</td>
                    <td>{formatNumber(catalog)}</td>
                    <td>{formatNumber(testAvg)}</td>
                    <td className="fw-bolder">{driftPct != null ? `${driftPct}%` : "—"}</td>
                    <td>{formatNumber(delta)}</td>
                    <td>{item.testCount ?? "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {items.length > 0 && (
        <div className="converter-testing-pagination">
          <button
            type="button"
            className="converter-testing-page-btn converter-testing-page-btn-prev"
            disabled={offset === 0}
            onClick={onPrev}
          >
            Previous
          </button>
          <button
            type="button"
            className="converter-testing-page-btn converter-testing-page-btn-next"
            disabled={!areMoreItemsAvailable}
            onClick={onNext}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
